import Router from 'next/router';
import React, { useMemo } from 'react';

import {
  BASE_PATH_2_QUERY,
  EDUCATION_EXP_QUERY,
  TEMPLATE_QUERY,
  TEMPLATE_VIEW_QUERY,
  WORK_EXP_QUERY,
} from '@glass/common/modules/pages/queryParams';
import ButtonIcon from '@glass/web/components/Button/ButtonIcon';
import ArrowForwardIos from '@glass/web/components/base/icons/ArrowForwardIos';
import { CONTACT_SECTION, TEMPLATES_SECTION } from '@glass/web/modules/resumes/router/sections';
import useClientReadyRouter from '@glass/web/modules/router/useClientReadyRouter';
import { MD } from '@glass/web/modules/theme/breakpointKeys';
import { RIGHT } from '@glass/web/modules/theme/constants';
import { FLEX } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';
import { EXTRA_SMALL_SPACING, SMALL_SPACING } from '@glass/web/modules/theme/spacing';
import lightAppBar from '@glass/web/modules/theme/styles/lightAppBar';

import Button from '@glass/shared/components/Button/Button';
import ResumeBuilderLink from '@glass/shared/components/Link/ResumeBuilderLink/ResumeBuilderLink';
import getBuilderSectionStepParams from '@glass/shared/components/Link/ResumeBuilderLink/getBuilderSectionStepParams';
import BuilderBackButton from '@glass/shared/components/ResumeBuilder/BuilderBackButton';
import BuilderLayoutPage from '@glass/shared/components/ResumeBuilder/BuilderLayoutPage';
import TemplateChooserView from '@glass/shared/components/ResumeBuilder/pages/templates/TemplateChooserView';
import usePrefetchNextBuilderStep from '@glass/shared/components/ResumeBuilder/usePrefetchNextBuilderStep';
import EducationExperienceSection from '@glass/shared/components/Section/ExperienceSection/EducationExperienceSection';
import WorkExperienceSection, {
  MINIMAL_WORK_EXP_QUERY_VALUE,
  SKIP_QUERY_VALUE,
} from '@glass/shared/components/Section/ExperienceSection/WorkExperienceSection';
import SlideContainer from '@glass/shared/components/Transition/SlideContainer';
import useBuilderQuery from '@glass/shared/components/hooks/useBuilderQuery';
import useActiveResume from '@glass/shared/modules/resumes/hooks/useActiveResume';

const useStyles = makeStyles()((theme) => ({
  skipButton: {
    marginLeft: 'auto',
  },
  buttonContainer: {
    marginTop: theme.spacing(SMALL_SPACING),
    display: FLEX,
    [theme.breakpoints.down(MD)]: lightAppBar(theme),
  },
}));

function TemplateSelectorPage(props) {
  const { isClientReady, query: queryParams } = useClientReadyRouter();
  const {
    [WORK_EXP_QUERY]: workExpQuery,
    [EDUCATION_EXP_QUERY]: educationExpQuery,
    [TEMPLATE_VIEW_QUERY]: templateView,
  } = queryParams;

  const initialView = useMemo(() => {
    if ([TEMPLATE_QUERY, EDUCATION_EXP_QUERY, WORK_EXP_QUERY].includes(templateView)) {
      return templateView;
    }
    return WORK_EXP_QUERY;
  }, [templateView]);

  const { classes } = useStyles();
  useActiveResume();
  const query = useBuilderQuery(props);

  const view = useMemo(() => {
    if (!isClientReady || (!workExpQuery && initialView === WORK_EXP_QUERY)) {
      return initialView;
    }
    // we're going to go education only if minimal work experience due to tested CVR
    if (
      !educationExpQuery &&
      (workExpQuery === MINIMAL_WORK_EXP_QUERY_VALUE || initialView === EDUCATION_EXP_QUERY)
    ) {
      return EDUCATION_EXP_QUERY;
    }
    return TEMPLATE_QUERY;
  }, [isClientReady, workExpQuery, educationExpQuery, initialView]);

  const skipParams = useMemo(() => {
    if (view === TEMPLATE_QUERY) {
      return getBuilderSectionStepParams({ ...queryParams, [BASE_PATH_2_QUERY]: CONTACT_SECTION });
    }

    return getBuilderSectionStepParams({
      ...queryParams,
      [BASE_PATH_2_QUERY]: TEMPLATES_SECTION,
      [view]: SKIP_QUERY_VALUE,
    });
  }, [queryParams, view]);

  usePrefetchNextBuilderStep(skipParams);

  return (
    <BuilderLayoutPage query={query}>
      <SlideContainer transitionKey={view || WORK_EXP_QUERY}>
        {view === WORK_EXP_QUERY ? (
          <WorkExperienceSection
            disableCta
            isNoXPadding
            persistQueryParams
            shallow
            isAlternatingColor={false}
            overflow="hidden"
            spacing={EXTRA_SMALL_SPACING}
            title="How much experience do you have?"
            titleComponent="h1"
          />
        ) : null}
        {view === EDUCATION_EXP_QUERY ? (
          <EducationExperienceSection
            disableCta
            isNoXPadding
            persistQueryParams
            shallow
            isAlternatingColor={false}
            overflow="hidden"
            spacing={EXTRA_SMALL_SPACING}
            title="What's your education level?"
            titleComponent="h1"
          />
        ) : null}
        {view === TEMPLATE_QUERY ? <TemplateChooserView /> : null}

        <div className={classes.buttonContainer}>
          <BuilderBackButton onClick={Router.back} />
          <Button
            preventBlock
            className={classes.skipButton}
            color="inherit"
            component={ResumeBuilderLink}
            params={skipParams}
            shallow={skipParams[BASE_PATH_2_QUERY] === TEMPLATES_SECTION}
          >
            Skip
            <ButtonIcon fontSize={16} icon={ArrowForwardIos} position={RIGHT} />
          </Button>
        </div>
      </SlideContainer>
    </BuilderLayoutPage>
  );
}

export default TemplateSelectorPage;
